import React, { useCallback, useId, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ArrowLeft,
  GripVertical,
  Info,
  ListOrdered,
  Plus,
  Trash2
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, SectionHeader } from "@/components/ui/page-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { VISAVALUE } from "../../../types/visa.type";
import { DefaultTemplatesAPI } from "../../../api/default-templates";
import { SortableSection } from "../../../components/common/ManageSectionsModal";
import SectionContainer from "./SectionContainer";
import { ProseTextArea } from "../../../components/common/ProseTextArea";
import { DraftType, DRAFT_TYPE_LABELS } from "../../../api/types/draft";

type SidebarSortableItemProps = {
  id: string;
  title: string;
  isSelected: boolean;
  onClick: () => void;
};

const SidebarSortableItem: React.FC<SidebarSortableItemProps> = ({
  id,
  title,
  isSelected,
  onClick
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  } as React.CSSProperties;
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex h-8 cursor-pointer items-center gap-2 rounded-md px-2 transition-colors duration-100",
        isSelected
          ? "bg-fill-3 font-medium text-fg"
          : "text-fg-2 hover:bg-fill-1 hover:text-fg"
      )}
      onClick={onClick}
    >
      <span className="min-w-0 flex-1 truncate">{title}</span>
      <span
        {...attributes}
        {...listeners}
        className="flex cursor-grab text-fg-3 hover:text-fg"
      >
        <GripVertical className="size-3.5" />
      </span>
    </div>
  );
};

const InfoHint = ({ label, content }: { label: string; content: string }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        aria-label={label}
        className="flex rounded-xs text-fg-3 outline-none hover:text-fg focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        <Info className="size-3.5" />
      </button>
    </TooltipTrigger>
    <TooltipContent side="top">{content}</TooltipContent>
  </Tooltip>
);

const RequiredMark = () => (
  <span className="text-danger" aria-hidden>
    *
  </span>
);

export const CreateTemplate: React.FC = () => {
  const [visa, setVisa] = useState<VISAVALUE | "">("");
  const [visaSubtype, setVisaSubtype] = useState<string>("");
  const [draftType, setDraftType] = useState<DraftType | "">("");
  const [isStandard, setIsStandard] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [sections, setSections] = useState<any[]>([]);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [highlightedSection, setHighlightedSection] = useState<string | null>(
    null
  );
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [isOpen, setIsOpen] = useState(false);
  const onOpen = () => setIsOpen(true);
  const onClose = () => setIsOpen(false);
  const [sectionToDelete, setSectionToDelete] = useState<any | null>(null);
  const visaId = useId();
  const visaSubtypeId = useId();
  const draftTypeId = useId();
  const standardId = useId();

  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!visa || !draftType) {
      toast.error("Please fill required fields", { duration: 3000 });
      return;
    }
    setIsSubmitting(true);
    try {
      const createdTemplate = await DefaultTemplatesAPI.create({
        visa: visa as VISAVALUE,
        visa_subtype: visaSubtype || undefined,
        draft_type: draftType as DraftType,
        is_standard: isStandard,
        sections
      });
      toast.success("Template created", { duration: 3000 });
      if (createdTemplate.default_template_id) {
        navigate(
          `/admin/default-templates/${createdTemplate.default_template_id}`
        );
      } else {
        navigate("/admin/default-templates");
      }
    } catch (error) {
      toast.error("Creation failed", {
        description:
          error instanceof Error ? error.message : "An error occurred",
        duration: 5000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const reducedSections = useMemo(
    () =>
      sections.map((section, index) => ({
        id: section.id || `section_${index + 1}`,
        title: section.section_name || `Section ${index + 1}`
      })),
    [sections]
  );

  const toggleSectionFromHeader = (id: string) => {
    setExpandedSections((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const expandSectionFromSidebar = (id: string) => {
    setExpandedSections((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setHighlightedSection(id);
    setTimeout(() => {
      sectionRefs.current[id]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest"
      });
    }, 50);
  };

  const setSectionRef = useCallback(
    (id: string) => (el: HTMLDivElement | null) => {
      sectionRefs.current[id] = el;
    },
    []
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }
    })
  );

  const handleSidebarDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSections((prev) => {
      const oldIndex = prev.findIndex((s) => s.id === active.id);
      const newIndex = prev.findIndex((s) => s.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleSaveEditor = async (plainText: string, sectionId: string) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              section_content: plainText
            }
          : s
      )
    );
  };

  const handleSaveTitle = (sectionId: string, value: string) => {
    setSections((prev) =>
      prev.map((s) => (s.id === sectionId ? { ...s, section_name: value } : s))
    );
    toast.success("Section title updated", { duration: 2000 });
  };

  const handleSaveSortedSections = (updated: SortableSection[]) => {
    const newSections = updated.map((u) => {
      const original = sections.find((s) => s.id === u.id);
      return original
        ? { ...original, section_name: u.title }
        : {
            id: String(u.id),
            section_name: u.title,
            display_name: "",
            section_content: ""
          };
    });
    setSections(newSections);
    setExpandedSections(
      newSections.map((s) => s.id).filter(Boolean) as string[]
    );
  };

  const handleAddSection = () => {
    const newId = `section_${Date.now()}`;
    const newSection = {
      id: newId,
      section_name: `Section ${sections.length + 1}`,
      display_name: "",
      section_content: ""
    };
    setSections((prev) => [...prev, newSection]);
    setExpandedSections((prev) => [...prev, newId]);
  };

  const handleDeleteSection = (sectionId: string) => {
    setSections((prev) => prev.filter((s) => s.id !== sectionId));
    setExpandedSections((prev) => prev.filter((id) => id !== sectionId));
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-y-auto bg-surface">
      <div className="mx-auto flex w-full max-w-280 flex-col gap-8 px-12 pt-10 pb-24">
        <div className="flex flex-col gap-3">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link
                    to="/admin/default-templates"
                    className="inline-flex items-center gap-1"
                  >
                    <ArrowLeft className="size-3.5" />
                    Default Templates
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Create</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <PageHeader
            title="Create Default Template"
            description="Define a default template for a visa and draft type."
            actions={
              <Button
                form="create-default-template-form"
                type="submit"
                variant="primary"
                size="sm"
                disabled={isSubmitting || !visa || !draftType}
              >
                {isSubmitting ? <Spinner /> : null}
                {isSubmitting ? "Creating..." : "Create Default Template"}
              </Button>
            }
          />
        </div>

        <div className="flex items-start gap-8">
          <nav className="sticky top-6 flex w-52 shrink-0 flex-col gap-3">
            <SectionHeader title="Contents" />
            <div className="max-h-[70vh] overflow-y-auto pr-1">
              {sections.length > 0 ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleSidebarDragEnd}
                >
                  <SortableContext
                    items={sections.map((s) => s.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="flex flex-col gap-0.5">
                      {sections.map((section, index) => (
                        <SidebarSortableItem
                          key={section.id}
                          id={section.id}
                          title={section.section_name || `Section ${index + 1}`}
                          isSelected={
                            highlightedSection === section.id &&
                            expandedSections.includes(section.id)
                          }
                          onClick={() =>
                            section.id && expandSectionFromSidebar(section.id)
                          }
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                <p className="px-2 text-sm text-fg-3">No sections yet</p>
              )}
            </div>
          </nav>

          <div className="flex min-w-0 flex-1 flex-col gap-8">
            <section className="flex flex-col gap-3">
              <SectionHeader title="Details" />
              <form
                id="create-default-template-form"
                onSubmit={handleSubmit}
                className="flex flex-col gap-4 rounded-xl bg-elevated p-4 ring-1 ring-line-2"
              >
                <div className="flex flex-wrap items-start gap-4">
                  <div className="flex min-w-55 flex-1 flex-col gap-1.5">
                    <Label htmlFor={visaId}>
                      Visa <RequiredMark />
                    </Label>
                    <Select
                      required
                      value={visa || undefined}
                      onValueChange={(value) => setVisa(value as VISAVALUE)}
                      disabled={isSubmitting}
                    >
                      <SelectTrigger id={visaId} className="w-full">
                        <SelectValue placeholder="Select visa" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.values(VISAVALUE)
                          .filter((v) => v !== VISAVALUE.EMPTY)
                          .map((v) => (
                            <SelectItem key={v} value={v}>
                              {v}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex min-w-55 flex-1 flex-col gap-1.5">
                    <div className="flex items-center gap-1">
                      <Label htmlFor={visaSubtypeId}>Visa Subtype</Label>
                      <InfoHint
                        label="About visa subtypes"
                        content={
                          'For workflows that support subtypes (for example: "Asylum"), enter values like "Affirmative" or "Defensive" to distinguish draft variants.'
                        }
                      />
                    </div>
                    <Input
                      id={visaSubtypeId}
                      value={visaSubtype}
                      onChange={(e) => setVisaSubtype(e.target.value)}
                      placeholder="e.g., O-1A: research"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={draftTypeId}>
                    Draft Type <RequiredMark />
                  </Label>
                  <Select
                    required
                    value={draftType || undefined}
                    onValueChange={(value) => setDraftType(value as DraftType)}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger id={draftTypeId} className="w-full">
                      <SelectValue placeholder="Select draft type" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(DraftType).map((dt) => (
                        <SelectItem key={dt} value={dt}>
                          {DRAFT_TYPE_LABELS[dt as DraftType]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <Label htmlFor={standardId}>
                    Make this the standard template?
                  </Label>
                  <InfoHint
                    label="About standard templates"
                    content={
                      'If checked, this default template will appear with a "Standard" badge.'
                    }
                  />
                  <Switch
                    id={standardId}
                    className="ml-2"
                    checked={isStandard}
                    onCheckedChange={setIsStandard}
                    disabled={isSubmitting}
                  />
                </div>
              </form>
            </section>

            <section className="flex flex-col gap-3">
              <SectionHeader
                title="Sections"
                actions={
                  <Button type="button" size="sm" onClick={handleAddSection}>
                    <Plus />
                    Add Section
                  </Button>
                }
              />
              {sections.length === 0 ? (
                <EmptyState
                  icon={ListOrdered}
                  title="No Sections Yet"
                  description="Use Add Section to create one."
                  className="rounded-xl bg-elevated ring-1 ring-line-2"
                />
              ) : (
                <div className="flex flex-col gap-3">
                  {sections.map((section) => (
                    <SectionContainer
                      key={section.id}
                      title={section.section_name || "Untitled section"}
                      isExpanded={
                        section.id
                          ? expandedSections.includes(section.id)
                          : false
                      }
                      onToggle={() =>
                        section.id && toggleSectionFromHeader(section.id)
                      }
                      isTitleEditable
                      onSaveTitle={(value: string) =>
                        section?.id && handleSaveTitle(section.id, value)
                      }
                      headerRight={
                        <Button
                          type="button"
                          variant="danger"
                          size="icon-xs"
                          aria-label="Delete section"
                          onClick={() => {
                            setSectionToDelete(section);
                            onOpen();
                          }}
                        >
                          <Trash2 />
                        </Button>
                      }
                      ref={section.id ? setSectionRef(section.id) : undefined}
                    >
                      <ProseTextArea
                        key={section.id}
                        text={section.section_content || ""}
                        saveText={(plainText) =>
                          handleSaveEditor(plainText, section.id || "")
                        }
                        customHeight="auto"
                        shouldShowSaveBtn={false}
                        enableBracketHighlighting
                      />
                    </SectionContainer>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>

        <AlertDialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Section</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this section? This action cannot
                be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel size="sm">Cancel</AlertDialogCancel>
              <AlertDialogAction
                variant="destructive"
                size="sm"
                onClick={() => {
                  if (sectionToDelete?.id)
                    handleDeleteSection(sectionToDelete.id);
                  onClose();
                }}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};
